import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Calendar, Radio, Facebook, Youtube } from "lucide-react";

// Accepts any of the following from Facebook and returns a usable iframe src:
//  - Full iframe HTML snippet pasted from Facebook → extracts the src
//  - A direct plugins/video.php?href=... URL → returns as-is
//  - A canonical facebook.com video URL → wraps it in the plugin URL
//  - share/v/... links and anything else → returns null (caller falls back)
function getFacebookEmbedSrc(input) {
  if (!input) return null;
  const trimmed = String(input).trim();
  if (!trimmed) return null;

  // Case 1: pasted iframe HTML
  const iframeMatch = trimmed.match(/<iframe[^>]+src=["']([^"']+)["']/i);
  if (iframeMatch) return iframeMatch[1];

  // Case 2: already the plugin URL
  if (trimmed.includes("plugins/video.php")) return trimmed;

  // Case 3: canonical facebook.com video URL → wrap it
  if (/facebook\.com\/[^/]+\/videos\//i.test(trimmed) || /facebook\.com\/watch\//i.test(trimmed)) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(trimmed)}&show_text=0&autoplay=0`;
  }

  // Unsupported (e.g. share/v/...) — caller will fall back
  return null;
}

export default function Live() {
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState("youtube");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/live");
        setCfg(data);
        if (!data.youtube_live_id && data.facebook_live_url) setTab("facebook");
      } catch {}
    })();
  }, []);

  if (!cfg) return <div className="pt-32 ccn-container">Loading…</div>;

  const fbEmbedSrc = getFacebookEmbedSrc(cfg.facebook_live_url);

  return (
    <div data-testid="live-page" className="pt-28 pb-20 bg-[#2A2E2C] text-white min-h-screen">
      <div className="ccn-container">
        <div className="flex items-center gap-3 mb-4">
          {cfg.is_live ? (
            <span data-testid="live-indicator" className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-bold bg-[#FF3B30] px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse-live" /> Live now
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-bold bg-white/10 px-3 py-1.5 rounded-full">
              Offline
            </span>
          )}
        </div>
        <h1 className="ccn-h1 mb-4">{cfg.title || "Sunday Worship"}</h1>
        <div className="flex items-center gap-2 text-white/70 mb-10">
          <Calendar className="w-4 h-4" /> {cfg.schedule_note}
        </div>

        {(cfg.youtube_live_id || cfg.facebook_live_url) && (
          <div className="flex gap-2 mb-6">
            {cfg.youtube_live_id && (
              <button
                data-testid="live-tab-youtube"
                onClick={() => setTab("youtube")}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition ${tab === "youtube" ? "bg-[#C8664D] text-white" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
              >
                <Youtube className="w-4 h-4" /> YouTube
              </button>
            )}
            {cfg.facebook_live_url && (
              <button
                data-testid="live-tab-facebook"
                onClick={() => setTab("facebook")}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition ${tab === "facebook" ? "bg-[#C8664D] text-white" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
              >
                <Facebook className="w-4 h-4" /> Facebook
              </button>
            )}
          </div>
        )}

        <div className="rounded-2xl overflow-hidden bg-black aspect-[9/16] sm:aspect-video shadow-2xl mx-auto w-full max-w-[min(420px,100%)] sm:max-w-none">
          {tab === "youtube" && cfg.youtube_live_id ? (
            <iframe
              data-testid="youtube-live-player"
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${cfg.youtube_live_id}?autoplay=0&rel=0`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="YouTube Live"
            />
          ) : tab === "facebook" && fbEmbedSrc ? (
            <iframe
              data-testid="facebook-live-player"
              className="w-full h-full"
              src={fbEmbedSrc}
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              allowFullScreen
              title="Facebook Live"
            />
          ) : cfg.watch_live_url ? (
            <div className="w-full h-full flex flex-col items-center justify-center text-center p-10" data-testid="live-watch-cta">
              <Facebook className="w-16 h-16 text-[#DDA754] mb-5" />
              <div className="font-heading text-3xl mb-3">Join us live on Facebook</div>
              <p className="text-white/70 max-w-lg mb-8">
                Our service streams on Facebook every {cfg.schedule_note || "Monday"}. Click below to watch.
              </p>
              <a
                href={cfg.watch_live_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="live-page-watch-fb-btn"
                className="ccn-btn-primary"
              >
                <Facebook className="w-4 h-4" /> Watch on Facebook
              </a>
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-center p-10">
              <Radio className="w-12 h-12 text-[#DDA754] mb-4" />
              <div className="font-heading text-3xl mb-3">Stream not yet configured</div>
              <p className="text-white/70 max-w-md">
                We'll be live every {cfg.schedule_note}. Admins can set the YouTube Live ID, Facebook Live URL, or Watch Live URL from the dashboard.
              </p>
            </div>
          )}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div data-testid="live-card-inspiration" className="rounded-2xl p-6 bg-[#1B3A66] border border-[#DDA754]/30 text-white shadow-lg">
            <Calendar className="w-6 h-6 text-[#DDA754] mb-3" />
            <div className="font-heading text-xl mb-1">Inspiration for Life</div>
            <div className="text-sm text-white/85">Live on Facebook · Mondays · 7 PM CST / 8 PM EST</div>
          </div>
          <div data-testid="live-card-friday" className="rounded-2xl p-6 bg-[#1B3A66] border border-[#DDA754]/30 text-white shadow-lg">
            <Radio className="w-6 h-6 text-[#DDA754] mb-3" />
            <div className="font-heading text-xl mb-1">Friday Night Highlights</div>
            <div className="text-sm text-white/85">On YouTube · Fridays · 9 PM CST / 10 PM EST</div>
          </div>
          <a
            href="https://linktr.ee/christiancovenantnetwork"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="live-card-linktree"
            className="rounded-2xl p-6 bg-[#1B3A66] border border-[#DDA754]/30 text-white shadow-lg block transition-transform hover:-translate-y-1"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-6 h-6 text-[#DDA754] mb-3"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M13.73635 5.85251 17.5 2.11168l2.50094 2.50094-3.85651 3.74052H21.51v3.5398h-5.37348l3.86848 3.74052L17.5 18.13721 12 12.61131l-5.49999 5.5259-2.50095-2.51289 3.86848-3.74052H2.49v-3.5398h5.37348L4.00691 4.61262l2.50095-2.50094 3.74052 3.74083V.45508h3.48797v5.39743ZM10.24535 15.5158h3.48797V24h-3.48797v-8.4842Z" />
            </svg>
            <div className="font-heading text-xl mb-1">Follow us on all platforms</div>
            <div className="text-sm text-white/85 break-words">linktr.ee/christiancovenantnetwork</div>
          </a>
        </div>
      </div>
    </div>
  );
}
